import {useCallback, useEffect, useState} from 'react';
import {useMsal} from '@azure/msal-react';
import {InteractionStatus} from '@azure/msal-browser';
import {loginRequest} from '@/helpers/Auth/authService';
import {ROUTES} from '@/utils/constants/routesConstants';
import {useAppDispatch, useAppSelector} from '@/store';
import {
  selectShellActive,
  setToastNotifications
} from '@/store/slices/commonSlice';
import {logger} from '@/helpers/logger';

const decodeJwt = (token) => {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;

  try {
    const base64 = parts[1].replaceAll('-', '+').replaceAll('_', '/');
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      '='
    );
    const decoded = decodeURIComponent(
      atob(padded)
        .split('')
        .map(
          (char) =>
            `%${(char.codePointAt(0) ?? 0).toString(16).padStart(2, '0')}`
        )
        .join('')
    );

    return JSON.parse(decoded);
  } catch {
    return null;
  }
};

export const getUserInfo = () => {
  const userClaims = decodeJwt(localStorage.getItem('msalAccessToken')) || {};

  const roleFromAccessRules =
    typeof userClaims.accessRules === 'string'
      ? (() => {
          try {
            return JSON.parse(userClaims.accessRules)?.role;
          } catch {
            return undefined;
          }
        })()
      : userClaims.accessRules?.role;

  const fullName =
    userClaims.name ||
    [userClaims.firstName, userClaims.lastName].filter(Boolean).join(' ') ||
    '';

  return {
    email: userClaims.email || userClaims.preferred_username || '',
    name: fullName,
    role: userClaims.roles || roleFromAccessRules || ''
  };
};

export const useAuth = () => {
  const dispatch = useAppDispatch();
  const {instance, accounts, inProgress} = useMsal();
  const shellActive = useAppSelector(selectShellActive);
  const [accessToken, setAccessToken] = useState();
  const [isReady, setIsReady] = useState(false);

  const signInWithRedirect = () => {
    instance.loginRedirect(loginRequest);
  };

  const signOut = useCallback(() => {
    localStorage.removeItem('msalAccessToken');
    instance.logoutRedirect({
      postLogoutRedirectUri: ROUTES.HOME
    });
  }, [instance]);

  const fetchToken = useCallback(async () => {
    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0]
      });
      setAccessToken(response.accessToken);
      localStorage.setItem('msalAccessToken', response.idToken); //here idToken is used to authenticate APIs
    } catch (error) {
      if (
        ['interaction_required', 'invalid_grant', 'no_tokens_found'].includes(
          error.errorCode
        )
      ) {
        // Token is expired or interaction is required, prompt user to login again
        instance.loginRedirect(loginRequest);
      } else if (error.errorCode === 'monitor_window_timeout') {
        // If we're facing a timeout issue, better to do a complete logout
        // to avoid potential redirect loops
        logger.warn('Authentication window timed out, logging user out');
        dispatch(
          setToastNotifications({
            show: true,
            type: 'error',
            message: 'Sign-in timed out. Please try again.'
          })
        );
      } else {
        logger.error('Error fetching token:', error);
        dispatch(
          setToastNotifications({
            show: true,
            type: 'error',
            message: 'Sign-in failed. Please try again.'
          })
        );
      }
      signOut();
    } finally {
      setIsReady(true);
    }
  }, [accounts, instance, dispatch, signOut]);

  const handleLogin = (loginType) => {
    if (inProgress === InteractionStatus.None) {
      if (loginType === 'popup') {
        instance.loginPopup(loginRequest);
      } else {
        instance.loginRedirect(loginRequest).catch(() => {
          dispatch(
            setToastNotifications({
              show: true,
              type: 'error',
              message: 'Sign-in failed. Please try again.'
            })
          );
        });
      }
    }
  };

  useEffect(() => {
    if (accounts.length > 0) {
      fetchToken();
    } else if (inProgress === InteractionStatus.None) {
      // No account to fetch a token for, and MSAL is done initializing.
      setIsReady(true);
    }
  }, [accounts, fetchToken, inProgress]);

  return {
    authState: inProgress,
    instance,
    accessToken,
    handleLogin,
    signInWithRedirect,
    signOut,
    account: accounts[0],
    isReady: shellActive ? true : isReady,
    isAuthenticated: shellActive ? true : accounts.length > 0,
    claims: accounts[0]?.idTokenClaims || {}
  };
};
